/**
 * Slice VI-01 · LeviReviewConversation — Levi-led post-walk review.
 *
 * Locked Product Owner direction embodied here:
 *  - Levi leads the experience. The contractor should feel like they
 *    are walking the job with an experienced project manager, not
 *    reading a technical report.
 *  - Contractor-facing wording NEVER uses the internal term
 *    "measurement candidate". Every user-visible string uses
 *    "Levi measurement" / "preliminary measurement" / "measurement
 *    awaiting your decision".
 *  - No six prominent disabled buttons. One restrained notice
 *    communicates that review-and-approval controls are coming in
 *    the next update.
 *  - Read-only in VI-01: no accept / verify / correct / reject /
 *    recapture / leave-preliminary mutations here.
 *  - No downstream artifact creation. This surface never drafts an
 *    estimate line, sends a customer message, or promotes anything
 *    into work_areas.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Sparkles, Ruler, MessageCircleQuestion, ChevronDown, ChevronUp,
  Video, Info,
} from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

const CONFIDENCE_STYLES = {
  high: {
    label_key: "vi01.confidence_high",
    dot: "bg-[var(--jc-green)]",
  },
  moderate: {
    label_key: "vi01.confidence_moderate",
    dot: "bg-[var(--jc-yellow)]",
  },
  low: {
    label_key: "vi01.confidence_low",
    dot: "bg-[var(--jc-red)]",
  },
};

const formatTimestamp = (seconds) => {
  if (seconds === null || seconds === undefined) return null;
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${String(rem).padStart(2, "0")}`;
};

const ConfidenceBadge = ({ level, t }) => {
  const style = CONFIDENCE_STYLES[level] || CONFIDENCE_STYLES.low;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-semibold text-[var(--jc-text-soft)]"
      data-testid={`vi01-confidence-${level}`}
    >
      <span className={`inline-block w-2 h-2 rounded-full ${style.dot}`} />
      {t(style.label_key)}
    </span>
  );
};

const EvidenceRow = ({ item, t }) => {
  const ev = (item.evidence && item.evidence[0]) || null;
  const t_start = ev?.t_start;
  const stamp = formatTimestamp(t_start);
  if (!stamp) return null;
  return (
    <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-[var(--jc-text-soft)]">
      <Video size={11} /> {t("vi01.evidence_at")} {stamp}
    </div>
  );
};

export default function LeviReviewConversation({ session }) {
  const { t } = useLang();
  const [view, setView] = useState("summary"); // summary | measurements | observations
  const [findings, setFindings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEvidencePanel, setShowEvidencePanel] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/video-walks/${session.id}/vision-review`);
        if (!cancelled) setFindings(r.data);
      } catch {
        if (!cancelled) setFindings(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [session.id]);

  const measurements = findings?.measurement_candidates || [];
  const observations = findings?.observations || [];

  const workAreaCount = useMemo(() => {
    // Approximate: distinct label prefixes on measurement candidates.
    const prefixes = new Set();
    measurements.forEach((m) => {
      const label = (m.label || "").split(" ")[0].toLowerCase();
      if (label) prefixes.add(label);
    });
    return prefixes.size;
  }, [measurements]);

  const highConfidenceMeasurements = measurements.filter(
    (m) => m.confidence === "high"
  );
  const uncertainMeasurements = measurements.filter(
    (m) => m.confidence !== "high"
  );

  if (loading) {
    return (
      <div className="jc-card p-5" data-testid="vi01-review-loading">
        <div className="text-sm text-[var(--jc-text-soft)]">
          {t("vi01.loading")}
        </div>
      </div>
    );
  }

  if (!findings || (measurements.length === 0 && observations.length === 0)) {
    // Nothing extracted yet — Levi's voice still leads. This is not a
    // blank card; it's a short conversational note.
    return (
      <div className="jc-card p-5" data-testid="vi01-review-empty">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-blue-100 p-2 shrink-0">
            <Sparkles size={16} className="text-blue-700" />
          </div>
          <div className="flex-1">
            <div className="overline">{t("vi01.overline")}</div>
            <div className="mt-1 text-base font-display">
              {t("vi01.empty_headline")}
            </div>
            <p className="mt-1 text-sm text-[var(--jc-text-soft)]">
              {t("vi01.empty_body")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="jc-card p-5 border-l-4 border-l-[var(--jc-blue)]"
         data-testid="vi01-review-conversation">
      {/* Levi speaks first — conversational summary. */}
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-blue-100 p-2 shrink-0">
          <Sparkles size={16} className="text-blue-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline">{t("vi01.overline")}</div>
          <div className="mt-1 text-base font-display leading-snug"
               data-testid="vi01-review-headline">
            {t("vi01.headline", {
              work_areas: workAreaCount,
              measurements: measurements.length,
              observations: observations.length,
            })}
          </div>
          {highConfidenceMeasurements.length > 0 && (
            <p className="mt-2 text-sm text-[var(--jc-text)]"
               data-testid="vi01-review-confidence-note">
              {t("vi01.confidence_summary", {
                high: highConfidenceMeasurements.length,
                uncertain: uncertainMeasurements.length,
              })}
            </p>
          )}
          <p className="mt-2 text-sm text-[var(--jc-text-soft)]">
            {t("vi01.review_prompt")}
          </p>

          {/* Two conversational navigation choices (per PO §5). */}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setView("measurements")}
              className={
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold " +
                (view === "measurements"
                  ? "bg-[var(--jc-blue)] text-white"
                  : "border border-[var(--jc-border)] text-[var(--jc-text)]")
              }
              data-testid="vi01-review-choose-measurements"
            >
              <Ruler size={12} /> {t("vi01.choice_measurements")}
            </button>
            <button
              type="button"
              onClick={() => setView("observations")}
              className={
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold " +
                (view === "observations"
                  ? "bg-[var(--jc-blue)] text-white"
                  : "border border-[var(--jc-border)] text-[var(--jc-text)]")
              }
              data-testid="vi01-review-choose-observations"
            >
              <MessageCircleQuestion size={12} /> {t("vi01.choice_observations")}
            </button>
          </div>

          {/* Levi conversation body. */}
          {view === "measurements" && (
            <div className="mt-4 space-y-2" data-testid="vi01-review-measurements-body">
              <div className="text-xs uppercase tracking-wide text-[var(--jc-text-soft)]">
                {t("vi01.measurements_section_title")}
              </div>
              {measurements.length === 0 && (
                <p className="text-sm text-[var(--jc-text-soft)]">
                  {t("vi01.measurements_empty")}
                </p>
              )}
              {measurements.map((m) => (
                <div key={m.id}
                     className="border border-[var(--jc-border)] rounded-md p-3"
                     data-testid={`vi01-preliminary-measurement-${m.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium">
                        {t("vi01.preliminary_measurement_label", {
                          label: m.label || m.unit,
                        })}
                      </div>
                      <div className="text-sm text-[var(--jc-text-soft)]">
                        {m.quantity} {m.unit}
                        {m.formula ? ` · ${m.formula}` : ""}
                      </div>
                      <EvidenceRow item={m} t={t} />
                    </div>
                    <ConfidenceBadge level={m.confidence} t={t} />
                  </div>
                </div>
              ))}
            </div>
          )}
          {view === "observations" && (
            <div className="mt-4 space-y-2" data-testid="vi01-review-observations-body">
              <div className="text-xs uppercase tracking-wide text-[var(--jc-text-soft)]">
                {t("vi01.observations_section_title")}
              </div>
              {observations.length === 0 && (
                <p className="text-sm text-[var(--jc-text-soft)]">
                  {t("vi01.observations_empty")}
                </p>
              )}
              {observations.map((o) => (
                <div key={o.id}
                     className="border border-[var(--jc-border)] rounded-md p-3"
                     data-testid={`vi01-observation-${o.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-[var(--jc-text)]">
                        {o.text}
                      </div>
                      <EvidenceRow item={o} t={t} />
                    </div>
                    <ConfidenceBadge level={o.confidence} t={t} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Restrained "coming next" note — PO §2A. No six disabled
              buttons. One line the contractor can read and move on. */}
          <div className="mt-5 flex items-start gap-2 rounded-md bg-[var(--jc-surface-soft,rgba(0,0,0,0.02))] px-3 py-2 border border-dashed border-[var(--jc-border)]"
               data-testid="vi01-decision-notice">
            <Info size={13} className="text-[var(--jc-text-soft)] mt-0.5" />
            <p className="text-[11px] text-[var(--jc-text-soft)]">
              {t("vi01.decision_notice")}
            </p>
          </div>

          {/* Secondary structured evidence disclosure — collapsed by
              default. Never the lead surface. */}
          <button
            type="button"
            onClick={() => setShowEvidencePanel(!showEvidencePanel)}
            className="mt-4 inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"
            data-testid="vi01-evidence-panel-toggle"
          >
            {showEvidencePanel ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {showEvidencePanel
              ? t("vi01.hide_evidence_panel")
              : t("vi01.show_evidence_panel")}
          </button>
          {showEvidencePanel && (
            <div className="mt-2 rounded-md border border-[var(--jc-border)] p-3 text-[11px] text-[var(--jc-text-soft)] space-y-2"
                 data-testid="vi01-evidence-panel">
              <div>
                <span className="font-semibold">
                  {t("vi01.evidence_panel_source")}:
                </span>{" "}
                {findings.source}
              </div>
              <div>
                <span className="font-semibold">
                  {t("vi01.evidence_panel_policy")}:
                </span>{" "}
                {findings.usage_guard_policy_version}
              </div>
              <p>{t("vi01.evidence_panel_footnote")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
